import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import { SearchOutlined } from '@mui/icons-material';
import { Button, IconButton, InputAdornment, Stack } from '@mui/material';
import { useFormikContext } from 'formik';
import { enqueueSnackbar } from 'notistack';

import { CondicaoPagamentoAutoComplete } from 'components/autocompletes';
import { MarcaAutoComplete } from 'components/autocompletes/MarcaAutoComplete';
import { CissNumberField, CissTextField } from 'components/fields';
import { ContentModal } from 'components/modal';
import { SubTitle } from 'components/text';
import { ListagemProvider, useListagemContext } from 'context/ListagemContext';
import { CondicaoPagamento } from 'pages/complementar/condicao-pagamento/types';
import { ConfigPedidoQuebrasDinamicasModalGrid } from 'pages/compra/configuracao-pedido/components';
import { ConfigPedidoQuebrasDinamicas, Produto, ProdutoQuebra, ProdutosProps } from 'pages/compra/configuracao-pedido/types';

type ConfigPedidoQuebrasDinamicasModalProps = {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
};

/**
 * Gera novas quebras para um produto.
 */
const getNewQuebras = (produto: Produto, condicaoPagamento: CondicaoPagamento, qtQuebra: number) => {
    const newQuebras: ProdutoQuebra[] = [];
    const now = new Date();
    const date = new Date(now.getTime() - now.getTimezoneOffset() * 60 * 1000).toISOString();

    for (let i = 1; i <= qtQuebra; i++) {
        const peQuebra = Math.floor(100 / qtQuebra);

        newQuebras.push({
            produto: produto,
            qtQuebra: qtQuebra,
            condicaoPagamento: condicaoPagamento,
            nrQuebra: i,
            peQuebra: i !== qtQuebra ? peQuebra : 100 - peQuebra * qtQuebra + peQuebra,
            dtInicio: date,
            dtFim: date,
            fgAtivo: true,
        });
    }

    return newQuebras;
};

function ConfigPedidoQuebrasDinamicasModalContent({ open, setOpen }: ConfigPedidoQuebrasDinamicasModalProps) {
    const { setPage } = useListagemContext();
    const [valueFilter, setValueFilter] = useState('');
    const [selectedMarca, setSelectedMarca] = useState<{ idMarca: number } | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<Produto | null>(null);
    const [condicaoPagamento, setCondicaoPagamento] = useState<CondicaoPagamento | null>(null);
    const [qtQuebra, setQtQuebra] = useState<number>(0);
    const produtoMapRef = useRef<Map<number, ProdutosProps>>(new Map());
    const inputRef = useRef<HTMLInputElement>(null);
    const { setFieldValue, values } = useFormikContext<ConfigPedidoQuebrasDinamicas>();

    const handleSetValueFilter = (value: string) => {
        setValueFilter(value);
        setPage(1);
    };

    /**
     * Adiciona o produto ao form.
     */
    const addProduct = () => {
        if (selectedProduct && condicaoPagamento) {
            const productInList = values.configPedidoQuebrasDinamicas.some(
                (produtoQuebra: ProdutoQuebra) => produtoQuebra.produto.idProduto === selectedProduct.idProduto,
            );

            if (productInList) {
                enqueueSnackbar('Produto já possui quebras cadastradas', { variant: 'error' });
            } else {
                const newQuebras = getNewQuebras(selectedProduct, condicaoPagamento, qtQuebra);

                setFieldValue('configPedidoQuebrasDinamicas', [...values.configPedidoQuebrasDinamicas, ...newQuebras]);

                setOpen(false);
            }
        }
    };

    /**
     * Reseta os valores do modal.
     */
    const resetValues = useCallback(() => {
        if (!open) {
            setSelectedProduct(null);
            setSelectedMarca(null);
            setCondicaoPagamento(null);
            setQtQuebra(0);
            setValueFilter('');
            setPage(1);
            produtoMapRef.current.clear();
        }
    }, [open, setPage]);

    useEffect(resetValues, [resetValues]);

    return (
        <ContentModal
            closeButton
            open={open}
            setOpen={setOpen}
            sx={{
                padding: 2,
                borderRadius: 1,
                width: {
                    xs: '90vw',
                    md: 800,
                },
            }}
        >
            <>
                <SubTitle label="Incluir Quebra" />

                <Stack direction="row" sx={{ gap: 1, mb: 2, alignItems: 'center' }}>
                    <MarcaAutoComplete
                        size="small"
                        label="Marca"
                        sx={{ flex: 1 }}
                        value={selectedMarca}
                        onChange={(_e, value) => {
                            setSelectedMarca(value);
                            setPage(1);
                        }}
                    />

                    <CissTextField
                        inputRef={inputRef}
                        placeholder="Pesquisar por código ou nome"
                        label="Pesquisar"
                        size="small"
                        sx={{ flex: 1 }}
                        deboundTime={500}
                        value={valueFilter}
                        onChange={(e) => handleSetValueFilter(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();

                                if (inputRef.current) {
                                    handleSetValueFilter(inputRef.current.value);
                                }
                            }
                        }}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            edge="end"
                                            onClick={() => {
                                                if (inputRef.current) {
                                                    handleSetValueFilter(inputRef.current.value);
                                                }
                                            }}
                                        >
                                            <SearchOutlined />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Stack>

                <ConfigPedidoQuebrasDinamicasModalGrid
                    open={open}
                    selectedProduct={selectedProduct}
                    setSelectedProduct={setSelectedProduct}
                    selectedMarca={selectedMarca}
                    valueFilter={valueFilter}
                    produtoMapRef={produtoMapRef}
                />

                <Stack direction="row" sx={{ gap: 1, mt: 2 }}>
                    <CissNumberField
                        label="Quantidade de quebras"
                        size="small"
                        value={qtQuebra}
                        sx={{ flex: 1 }}
                        onChange={(e) => setQtQuebra(Number(e.target.value))}
                    />

                    <CondicaoPagamentoAutoComplete
                        sx={{ flex: 1 }}
                        size="small"
                        value={condicaoPagamento}
                        onChange={(_e, value) => setCondicaoPagamento(value)}
                    />
                </Stack>

                <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="outlined" onClick={() => setOpen(false)}>
                        Cancelar
                    </Button>

                    <Button variant="contained" onClick={addProduct} disabled={!selectedProduct || !qtQuebra || !condicaoPagamento}>
                        Incluir
                    </Button>
                </Stack>
            </>
        </ContentModal>
    );
}

export function ConfigPedidoQuebrasDinamicasModal(props: ConfigPedidoQuebrasDinamicasModalProps) {
    return (
        <ListagemProvider>
            <ConfigPedidoQuebrasDinamicasModalContent {...props} />
        </ListagemProvider>
    );
}
